import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import { Link, useMatch, useParams } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import {
  ToggleGroup,
  CalendarIcon,
  ListIcon,
  SquareKanbanIcon,
  useLocalStorage,
} from '@buffer-mono/popcorn'

import { isPostTab } from '~publish/components/PostTabs'
import type { PostTab } from '~publish/components/PostTabs'
import { useBoardViewEnabled } from '~publish/hooks/useBoardViewEnabled'
import useMatchCurrentOrParentPath from '~publish/hooks/useMatchCurrentOrParentPath'
import {
  newCalendarSingleChannel,
  newCalendarAllChannels,
  allChannels,
  channel,
  scheduleCalendar,
  schedule,
  scheduleBoard,
  channelSchedule,
  channelScheduleBoard,
} from '~publish/legacy/routes'
import { useCalendarFilters } from '~publish/pages/Calendar/hooks/useCalendarFilters'

import {
  DEFAULT_VIEW_MODE,
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '../Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '../Calendar/hooks/useCalendarDateRangeViewMode'

import styles from './PostListOrCalendarViewToggle.module.css'

export const LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY = 'post-list-view-mode'
export const DEFAULT_POST_LIST_VIEW_MODE = 'list'

// Neither Calendar nor Board carry a `tab` (List's Queue/Drafts/Approvals/Sent
// selection is meaningless on either), so switching away from List and back
// has nothing in the URL to restore it from. Remember the last seen tab here
// instead, mirroring LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY above.
export const LAST_POST_LIST_TAB_LOCAL_STORAGE_KEY = 'post-list-tab'

export type ListOrCalendarViewMode = 'list' | 'calendar'

type ToggleOption = {
  title: string
  key: string
  href: string
  selected: boolean
  icon: JSX.Element
}

/**
 * Get the calendar route for a single channel.
 * Always uses /channels/:id/calendar/:granularity (works for both new IA and legacy contexts)
 */
const getSingleChannelCalendarRoute = (
  channelId: string,
  viewMode: CalendarDateRangeViewMode,
  tagIds: string[] | undefined,
  postTypes: string[] | undefined,
): string => {
  return newCalendarSingleChannel.getRoute(channelId, viewMode, {
    tagIds,
    postTypes,
  })
}

/**
 * Get the calendar route for all channels based on the context.
 * Returns new IA route (/schedule/calendar) or legacy route (/all-channels/calendar)
 */
const getAllChannelsCalendarRoute = (
  isInScheduleContext: boolean,
  viewMode: CalendarDateRangeViewMode,
  channelIds: string[] | undefined,
  tagIds: string[] | undefined,
): string => {
  if (isInScheduleContext) {
    return scheduleCalendar.getRoute(viewMode, { channelIds, tagIds })
  }
  return newCalendarAllChannels.getRoute(viewMode, { channelIds, tagIds })
}

/**
 * Get the list route for all channels based on the context.
 * Returns new IA route (/schedule) or legacy route (/all-channels)
 */
const getAllChannelsListRoute = (
  isInScheduleContext: boolean,
  channelIds: string[] | undefined,
  tagIds: string[] | undefined,
  tab: PostTab | undefined,
): string => {
  if (isInScheduleContext) {
    return schedule.getRoute({ channelIds, tagIds, tab })
  }
  return allChannels.getRoute({ channelIds, tagIds, tab })
}

const useToggleOptions = (): ToggleOption[] => {
  const params = useParams<{ id?: string; granularity?: string }>()
  const isOnChannelRoute = useMatchCurrentOrParentPath(channel.route)
  const isOnChannelScheduleRoute = useMatchCurrentOrParentPath(
    channelSchedule.route,
  )
  // channelSchedule.route has no splat, so it doesn't match the board
  // sub-route — check that explicitly too, or the toggle (and the
  // isSingleChannelView/isInScheduleContext flags below) silently treat
  // /channels/:id/schedule/board as an all-channels route.
  const isOnChannelScheduleBoardRoute = useMatchCurrentOrParentPath(
    channelScheduleBoard.route,
  )
  const isCalendarInSingleChannelView = useMatchCurrentOrParentPath(
    newCalendarSingleChannel.route,
  )
  const isSingleChannelView =
    isOnChannelRoute ||
    isOnChannelScheduleRoute ||
    isOnChannelScheduleBoardRoute ||
    isCalendarInSingleChannelView
  const isCalendarInAllChannelsView = useMatchCurrentOrParentPath(
    newCalendarAllChannels.route,
  )
  // New IA routes (CORE-3371)
  const isCalendarInScheduleView = useMatchCurrentOrParentPath(
    scheduleCalendar.route,
  )

  // Detect if we're in the new IA schedule context (CORE-3371)
  const scheduleRouteMatch = useMatch('/schedule/*')
  const isInScheduleContext =
    !!scheduleRouteMatch ||
    isOnChannelScheduleRoute ||
    isOnChannelScheduleBoardRoute ||
    // /channels/:id/calendar has no /schedule-prefixed equivalent, so it
    // must count as schedule context on its own — otherwise switching to
    // Calendar on a single channel hides Board.
    isCalendarInSingleChannelView

  const { channelsFilter, tagsFilter, postTypesFilter } = useCalendarFilters()
  const channelIds = useMemo(() => {
    if (isSingleChannelView) {
      return [params.id]
    }
    return channelsFilter.queryParams
  }, [isSingleChannelView, params.id, channelsFilter])
  const tagIds = tagsFilter.queryParams
  // Carried across the toggle so a filtered view survives the switch. Only the
  // single-channel routes take it: the formats depend on one channel's service.
  const selectedPostTypes = postTypesFilter.selected
  const postTypes = useMemo(
    () => (selectedPostTypes.length ? [...selectedPostTypes] : undefined),
    [selectedPostTypes],
  )
  const [viewModeFromStorage] = useLocalStorage<CalendarDateRangeViewMode>(
    LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_VIEW_MODE,
  )

  // `tab` only ever appears in the URL on List itself; remember it here so
  // the List link built while on Calendar/Board can restore it.
  const [tabParam] = useQueryState('tab', parseAsString)
  const [lastTab, setLastTab] = useLocalStorage<PostTab | undefined>(
    LAST_POST_LIST_TAB_LOCAL_STORAGE_KEY,
    undefined,
  )
  useEffect(() => {
    if (tabParam && isPostTab(tabParam)) setLastTab(tabParam)
  }, [tabParam, setLastTab])
  const rememberedTab =
    tabParam && isPostTab(tabParam)
      ? tabParam
      : lastTab && isPostTab(lastTab)
        ? lastTab
        : undefined

  // Use granularity from URL if on calendar view, otherwise use localStorage
  const viewMode: CalendarDateRangeViewMode =
    params.granularity === 'month' || params.granularity === 'week'
      ? params.granularity
      : viewModeFromStorage

  const firstChannelId = channelIds?.[0]
  const calendarRoute = useMemo(() => {
    if (isSingleChannelView && firstChannelId) {
      return getSingleChannelCalendarRoute(
        firstChannelId,
        viewMode,
        tagIds,
        postTypes,
      )
    }

    return getAllChannelsCalendarRoute(
      isInScheduleContext,
      viewMode,
      channelIds?.filter((id): id is string => id !== undefined),
      tagIds,
    )
  }, [
    channelIds,
    tagIds,
    postTypes,
    isSingleChannelView,
    firstChannelId,
    viewMode,
    isInScheduleContext,
  ])

  // Use the correct list route based on context
  const allChannelsListRoute = getAllChannelsListRoute(
    isInScheduleContext,
    channelIds?.filter((id): id is string => id !== undefined),
    tagIds,
    rememberedTab,
  )

  // For single channel, use schedule route when in schedule context
  const channelScheduleMatch = useMatch('/channels/:id/schedule/*')
  const isInChannelScheduleContext =
    channelScheduleMatch && channelScheduleMatch.params.id === firstChannelId
  const singleChannelRoute = isInChannelScheduleContext
    ? channelSchedule.getRoute(firstChannelId ?? '', {
        tagIds,
        postTypes,
        tab: rememberedTab,
      })
    : channel.getRoute(firstChannelId ?? '', {
        tagIds,
        postTypes,
        tab: rememberedTab,
      })

  const isCalendar =
    isCalendarInSingleChannelView ||
    isCalendarInAllChannelsView ||
    isCalendarInScheduleView
  const listRoute = isSingleChannelView
    ? singleChannelRoute
    : allChannelsListRoute

  // Board is New-IA-only (no legacy /all-channels or /channels/:id
  // equivalent), so it's only offered as an option in schedule context.
  const isBoardInScheduleView = useMatchCurrentOrParentPath(scheduleBoard.route)
  const isBoard = isOnChannelScheduleBoardRoute || isBoardInScheduleView
  const isBoardViewEnabled = useBoardViewEnabled() && isInScheduleContext
  const boardRoute =
    isSingleChannelView && firstChannelId
      ? channelScheduleBoard.getRoute(firstChannelId)
      : scheduleBoard.getRoute()

  return [
    {
      title: 'List',
      key: 'all-channels',
      href: listRoute,
      selected: !isCalendar && !isBoard,
      icon: <ListIcon />,
    },
    ...(isBoardViewEnabled
      ? [
          {
            title: 'Board',
            key: 'board',
            href: boardRoute,
            selected: isBoard,
            icon: <SquareKanbanIcon />,
          },
        ]
      : []),
    {
      title: 'Calendar',
      key: 'calendar',
      href: calendarRoute,
      selected: isCalendar,
      icon: <CalendarIcon />,
    },
  ]
}

export type ViewToggleOption = {
  key: string
  title: string
  icon: JSX.Element
  href?: string
}

export const ViewToggle = ({
  options,
  value,
  onChange,
  className,
}: {
  options: ViewToggleOption[]
  value?: string
  onChange: (value: string) => void
  className?: string
}): JSX.Element => (
  <ToggleGroup
    value={value}
    size="medium"
    onChange={onChange}
    className={className}
  >
    {options.map((option) =>
      option.href ? (
        <ToggleGroup.Item
          key={option.key}
          value={option.key}
          asChild
          aria-label={option.title}
        >
          <Link to={option.href}>
            {option.icon}
            <span className={styles.label}>{option.title}</span>
          </Link>
        </ToggleGroup.Item>
      ) : (
        <ToggleGroup.Item
          key={option.key}
          value={option.key}
          aria-label={option.title}
        >
          {option.icon}
          <span className={styles.label}>{option.title}</span>
        </ToggleGroup.Item>
      ),
    )}
  </ToggleGroup>
)

export const PostListOrCalendarViewToggle = ({
  className,
}: {
  className?: string
}): JSX.Element => {
  const options = useToggleOptions()
  const selected = options.find((option) => option.selected) || options[0]

  const [, setPostListViewMode] = useLocalStorage<ListOrCalendarViewMode>(
    LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_POST_LIST_VIEW_MODE,
  )

  const handlePostListViewModeChange = (value: string): void => {
    if (value === 'calendar') {
      setPostListViewMode('calendar')
    } else if (value !== 'board') {
      // Board is its own route, not a List/Calendar preference — leave
      // that preference untouched when it's selected.
      setPostListViewMode('list')
    }
  }
  return (
    <ViewToggle
      options={options}
      value={selected?.key}
      onChange={handlePostListViewModeChange}
      className={className}
    />
  )
}
